/**
 * OccupancyPanel — hotel in-house figures on the Function Sheet (and the
 * Function Sheets daily screen view).
 *
 * Shows every night of the event (date → endDate, max 7), one clear line each:
 *   IN-HOUSE · Sat 17 Oct   18 rooms · 33 guests        [Edit]
 * Tap a line to enter / change the figures (screen only — prints clean).
 * Uses the shared occupancy module, so it never flashes "Loading" when the
 * page refreshes, and a save anywhere updates it straight away.
 */
import { useState, useMemo } from 'react';
import { BedDouble, Edit2, Plus } from 'lucide-react';
import { useOccupancy, formatOcc, enteredLabel, occWarning } from '../lib/occupancy';
import { parseLocalDate, toLocalDateKey, addDays } from '../lib/dateHelpers';
import OccupancyEditor from './OccupancyEditor';

export default function OccupancyPanel({ date, endDate }) {
  const nights = useMemo(() => {
    if (!date) return [];
    const out = [date];
    if (endDate && endDate > date) {
      for (let i = 1; i < 7; i++) {
        const k = toLocalDateKey(addDays(parseLocalDate(date), i));
        if (k > endDate) break;
        out.push(k);
      }
    }
    return out;
  }, [date, endDate]);
  const { occ, error } = useOccupancy(nights);
  const [editing, setEditing] = useState(null);

  if (!nights.length) return null;
  const multi = nights.length > 1;
  const dayLabel = (k) => parseLocalDate(k)?.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="rounded-lg border-2 border-sky-700 overflow-hidden print:border-black print:rounded-none">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-700 text-white print:bg-transparent print:text-black print:border-b print:border-black">
        <BedDouble size={15} className="print:hidden" />
        <span className="text-xs font-bold uppercase tracking-wider">Hotel in-house {multi ? '— each night' : '— night of event'}</span>
        {error && <span className="ml-auto text-xs font-normal text-red-100 print:hidden">could not load — refresh</span>}
      </div>
      {nights.map((k) => {
        const o = occ[k];
        const warn = o ? occWarning(o.rooms, o.guests) : '';
        return (
          <button key={k} type="button" onClick={() => setEditing(k)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left border-t border-sky-100 first:border-t-0 hover:bg-sky-50 print:border-gray-300 print:hover:bg-transparent">
            {multi && <span className="w-24 shrink-0 text-sm font-semibold text-gray-600 print:text-black">{dayLabel(k)}</span>}
            {o ? (
              <span className="text-xl font-bold text-gray-900 tabular-nums">{formatOcc(o)}</span>
            ) : o === null ? (
              <span className="text-base font-semibold text-amber-700 print:text-black">Not entered</span>
            ) : (
              <span className="text-base text-gray-400 print:hidden">Loading…</span>
            )}
            {warn && <span className="text-xs text-amber-700 print:hidden">⚠ check</span>}
            <span className="ml-auto flex items-center gap-2 text-xs text-gray-400 print:hidden">
              {enteredLabel(o)}
              <span className="flex items-center gap-1 text-sky-700 font-semibold">{o ? <><Edit2 size={13} />Edit</> : <><Plus size={13} />Add</>}</span>
            </span>
          </button>
        );
      })}
      {editing && <OccupancyEditor date={editing} current={occ[editing] || null} onClose={() => setEditing(null)} />}
    </div>
  );
}
